import React, { useState } from 'react';

// Assistant conversation en français pour capter des leads avec choix rapides
const ChatLeadBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const [answers, setAnswers] = useState({
    name: '',
    email: '',
    company: '',
    siteType: '',
    serviceType: '',
    details: '',
    timeline: ''
  });

  const siteOptions = ['Bureau', 'Restaurant', 'Airbnb', 'Hôtel', 'Entrepôt', 'Commerce', 'Autre'];
  const serviceOptions = [
    'Nettoyage de bureaux',
    'Nettoyage vitres',
    'Nettoyage sols',
    'Post-chantier',
    'Désinfection',
    'Nettoyage régulier',
    'Intervention ponctuelle'
  ];

  const steps = [
    {
      key: 'name',
      question: "Bonjour, je suis l'assistant E-omi. Comment vous appelez-vous ?",
      placeholder: 'Votre prénom et nom'
    },
    {
      key: 'email',
      question: 'Quelle est votre adresse e-mail pour vous répondre ? (professionnelle de préférence)',
      placeholder: 'prenom.nom@entreprise.fr',
      type: 'email'
    },
    {
      key: 'company',
      question: 'Votre entreprise ou secteur ? (optionnel)',
      placeholder: 'Nom de votre entreprise (optionnel)'
    },
    {
      key: 'siteType',
      question: "Quel type de site souhaitez-vous nettoyer ?",
      type: 'select',
      options: siteOptions
    },
    {
      key: 'serviceType',
      question: 'Quel service de nettoyage recherchez-vous ? (choisissez)',
      type: 'select',
      options: serviceOptions
    },
    {
      key: 'details',
      question: 'Ajoutez des précisions (surface, fréquence, accès, horaires) — optionnel',
      placeholder: 'Ex: 300 m², 2 fois/semaine, accès après 20h',
      multiline: true,
      optional: true
    },
    {
      key: 'timeline',
      question: 'Quand souhaitez-vous démarrer ? (date ou délai)',
      placeholder: 'Sous 48h, cette semaine, ce mois-ci...'
    }
  ];

  const currentStep = steps[step];

  const goNext = (updated) => {
    setAnswers(updated);
    setInput('');
    if (step === steps.length - 1) {
      submitLead(updated);
    } else {
      setStep(step + 1);
    }
  };

  const submitLead = async (updatedAnswers) => {
    setLoading(true);
    setError('');
    try {
      const composedNeed = `${updatedAnswers.serviceType || ''} - ${updatedAnswers.siteType || ''}${updatedAnswers.details ? ' | ' + updatedAnswers.details : ''}`.trim();
      const payload = {
        ...updatedAnswers,
        need: composedNeed || updatedAnswers.details || updatedAnswers.serviceType || 'Besoin non précisé',
        source: 'chatbot'
      };

      const response = await fetch('/api/lead-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Envoi échoué');
      }

      setSent(true);
    } catch (err) {
      setError("L'envoi a échoué, veuillez réessayer ou nous appeler au +33 6 60 88 64 51.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (currentStep.type === 'select') return;

    if (!input.trim() && !currentStep.optional) return;
    const value = input.trim();
    const updatedAnswers = { ...answers, [currentStep.key]: value };
    goNext(updatedAnswers);
  };

  const handleOptionSelect = (value) => {
    const updatedAnswers = { ...answers, [currentStep.key]: value };
    goNext(updatedAnswers);
  };

  const restart = () => {
    setStep(0);
    setInput('');
    setLoading(false);
    setError('');
    setSent(false);
    setAnswers({ name: '', email: '', company: '', siteType: '', serviceType: '', details: '', timeline: '' });
  };

  const renderSummary = () => (
    <div className="chatbot-summary">
      <p className="text-sm text-white-700 mb-2">Merci ! Nous avons bien reçu :</p>
      <ul className="text-sm text-white-800 space-y-1">
        <li><strong>Nom :</strong> {answers.name}</li>
        <li><strong>Email :</strong> {answers.email}</li>
        {answers.company && <li><strong>Entreprise :</strong> {answers.company}</li>}
        <li><strong>Site :</strong> {answers.siteType}</li>
        <li><strong>Service :</strong> {answers.serviceType}</li>
        {answers.details && <li><strong>Détails :</strong> {answers.details}</li>}
        <li><strong>Délai :</strong> {answers.timeline}</li>
      </ul>
      <p className="text-sm text-white-700 mt-3">Nous vous contacterons très vite à <strong>{answers.email}</strong>.</p>
      <button className="chatbot-button mt-3" onClick={restart}>
        Commencer une nouvelle demande
      </button>
    </div>
  );

  return (
    <div className="chatbot-wrapper">
      <button
        className="chatbot-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        {isOpen ? 'Fermer' : 'Chat Devis'}
      </button>

      {isOpen && (
        <div className="chatbot-panel shadow-xl">
          <div className="chatbot-header">
            <div>
              <p className="font-semibold text-white">Assistant E-omi</p>
                <p className="text-xs text-white-600">Obtenez un devis rapide en quelques questions</p>
            </div>
            <button className="chatbot-close" onClick={() => setIsOpen(false)} aria-label="Fermer le chatbot">
              ×
            </button>
          </div>

          {!sent ? (
            <form onSubmit={handleSubmit} className="chatbot-body">
              <div className="chatbot-question">{currentStep.question}</div>

              {currentStep.type === 'select' ? (
                <div className="grid grid-cols-1 gap-2">
                  {currentStep.options.map((opt) => (
                    <button
                      type="button"
                      key={opt}
                      className="chatbot-button chatbot-glass"
                      onClick={() => handleOptionSelect(opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              ) : currentStep.multiline ? (
                <textarea
                  className="chatbot-input h-24"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={currentStep.placeholder}
                  required={!currentStep.optional}
                />
              ) : (
                <input
                  className="chatbot-input"
                  type={currentStep.type || 'text'}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={currentStep.placeholder}
                  required={!currentStep.optional}
                />
              )}

              {error && <p className="chatbot-error">{error}</p>}
              {currentStep.type !== 'select' && (
                <button type="submit" className="chatbot-button chatbot-primary" disabled={loading}>
                  {loading ? 'Envoi...' : step === steps.length - 1 ? 'Envoyer' : 'Continuer'}
                </button>
              )}
            </form>
          ) : (
            renderSummary()
          )}
        </div>
      )}
    </div>
  );
};

export default ChatLeadBot;
